import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  RefreshCw,
  ListChecks,
  Languages,
  FileText,
  Settings2,
  FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const RULE_TYPES = [
  { id: 'language', name: 'Language Validation', icon: Languages, description: 'Verify document language matches metadata' },
  { id: 'title', name: 'Title Validation', icon: FileText, description: 'Check if title appears in document' },
  { id: 'content', name: 'Content Validation', icon: FileCheck, description: 'Compare extracted text with expected content' },
  { id: 'metadata', name: 'Metadata Validation', icon: Settings2, description: 'Validate required metadata fields' },
  { id: 'format', name: 'Format Validation', icon: ListChecks, description: 'Check document structure and formatting' },
];

const DEFAULT_CRITERIA = {
  language: {},
  title: { threshold: 70 },
  content: { threshold: 80, min_length: 10 },
  metadata: { required_fields: [] },
  format: { check_empty_pages: true, min_pages: 1, check_consistency: true },
};

export default function ValidationRules() {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('language');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formCriteria, setFormCriteria] = useState({});

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await api().get('/validation-rules');
      setRules(response.data);
    } catch (error) {
      toast.error('Failed to load validation rules');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRule(null);
    setFormName('');
    setFormType('language');
    setFormEnabled(true);
    setFormCriteria(DEFAULT_CRITERIA.language);
    setDialogOpen(true);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormName(rule.name);
    setFormType(rule.rule_type);
    setFormEnabled(rule.enabled);
    setFormCriteria(rule.criteria || DEFAULT_CRITERIA[rule.rule_type] || {});
    setDialogOpen(true);
  };

  const handleTypeChange = (type) => {
    setFormType(type);
    setFormCriteria(DEFAULT_CRITERIA[type] || {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName.trim()) {
      toast.error('Please enter a rule name');
      return;
    }
    
    try {
      const payload = {
        name: formName,
        rule_type: formType,
        enabled: formEnabled,
        criteria: formCriteria,
      };
      
      if (editingRule) {
        await api().put(`/validation-rules/${editingRule.id}`, payload);
        toast.success('Rule updated successfully');
      } else {
        await api().post('/validation-rules', payload);
        toast.success('Rule created successfully');
      }
      
      setDialogOpen(false);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      await api().delete(`/validation-rules/${id}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggle = async (rule) => {
    try {
      await api().put(`/validation-rules/${rule.id}`, {
        ...rule,
        enabled: !rule.enabled,
      });
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const getRuleIcon = (type) => {
    const config = RULE_TYPES.find(t => t.id === type);
    return config?.icon || ListChecks;
  };

  const renderCriteriaForm = () => {
    switch (formType) {
      case 'title':
      case 'content':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Match Threshold (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formCriteria.threshold || 80}
                onChange={(e) => setFormCriteria({ ...formCriteria, threshold: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">Minimum similarity percentage to pass validation</p>
            </div>
            {formType === 'content' && (
              <div className="space-y-2">
                <Label>Minimum Text Length</Label>
                <Input
                  type="number"
                  min="0"
                  value={formCriteria.min_length || 10}
                  onChange={(e) => setFormCriteria({ ...formCriteria, min_length: parseInt(e.target.value) })}
                />
              </div>
            )}
          </div>
        );
      
      case 'metadata':
        return (
          <div className="space-y-2">
            <Label>Required Fields (comma-separated)</Label>
            <Input
              placeholder="author, date, version"
              value={(formCriteria.required_fields || []).join(', ')}
              onChange={(e) => setFormCriteria({ 
                ...formCriteria, 
                required_fields: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
              })}
            />
            <p className="text-xs text-muted-foreground">Metadata fields that must be present</p>
          </div>
        );
      
      case 'format':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Check Empty Pages</Label>
                <p className="text-xs text-muted-foreground">Flag pages with no text</p>
              </div>
              <Switch
                checked={formCriteria.check_empty_pages !== false}
                onCheckedChange={(checked) => setFormCriteria({ ...formCriteria, check_empty_pages: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label>Minimum Pages</Label>
              <Input
                type="number"
                min="1"
                value={formCriteria.min_pages || 1}
                onChange={(e) => setFormCriteria({ ...formCriteria, min_pages: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Check OCR Consistency</Label>
                <p className="text-xs text-muted-foreground">Verify consistent extraction quality</p>
              </div>
              <Switch
                checked={formCriteria.check_consistency !== false}
                onCheckedChange={(checked) => setFormCriteria({ ...formCriteria, check_consistency: checked })}
              />
            </div>
          </div>
        );
      
      default:
        return (
          <p className="text-sm text-muted-foreground">
            No additional configuration required for this rule type.
          </p>
        );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="validation-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Validation Rules</h1>
          <p className="text-muted-foreground mt-1">Configure criteria for document validation</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="gap-2" data-testid="add-rule-btn">
              <Plus className="w-4 h-4" />
              Add Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingRule ? 'Edit Rule' : 'Create Rule'}
                </DialogTitle>
                <DialogDescription>
                  Configure validation criteria for documents
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Rule Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter rule name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    data-testid="rule-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Rule Type</Label>
                  <Select value={formType} onValueChange={handleTypeChange}>
                    <SelectTrigger data-testid="rule-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {RULE_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {RULE_TYPES.find(t => t.id === formType)?.description}
                  </p>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enabled</Label>
                    <p className="text-xs text-muted-foreground">Run this rule during validation</p>
                  </div>
                  <Switch
                    checked={formEnabled}
                    onCheckedChange={setFormEnabled}
                    data-testid="rule-enabled-switch"
                  />
                </div>
                
                <div className="border-t pt-4">
                  <Label className="mb-3 block">Criteria</Label>
                  {renderCriteriaForm()}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-rule-btn">
                  {editingRule ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <ListChecks className="w-5 h-5" />
            Active Rules ({rules.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : rules.length > 0 ? (
            <div className="space-y-4">
              {rules.map((rule) => {
                const Icon = getRuleIcon(rule.rule_type);
                return (
                  <div 
                    key={rule.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`rule-${rule.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{rule.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {RULE_TYPES.find(t => t.id === rule.rule_type)?.name || rule.rule_type}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {RULE_TYPES.find(t => t.id === rule.rule_type)?.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={rule.enabled}
                        onCheckedChange={() => handleToggle(rule)}
                        data-testid={`toggle-${rule.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(rule)}
                        data-testid={`edit-${rule.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rule.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-${rule.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <ListChecks className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-medium mb-2">No validation rules</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create rules to define validation criteria
              </p>
              <Button onClick={openCreateDialog} data-testid="empty-add-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Rule
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
